// Tank Levels — how much each tank holds now, what went in and what came out
// (client, 2026-10-24: "tank mein kitna bacha hai, kitna add kiya tha").
// Level = the last dip (Tank Register) + oil in − oil out since it. Inward into
// a tank and issues from a tank are counted on their own; Add / Take out /
// Transfer here cover the rest. Server: src/api-inventory/tank-levels.js.
import { useMemo, useState } from 'react';
import { ArrowDownToLine, ArrowRightLeft, ArrowUpFromLine, Gauge, History, Ruler } from 'lucide-react';
import toast from 'react-hot-toast';
import { Button } from '@/shared/components/ui/Button';
import { Input } from '@/shared/components/ui/Input';
import { Modal } from '@/shared/components/ui/Modal';
import { Select } from '@/shared/components/ui/Select';
import { Skeleton } from '@/shared/components/ui/Skeleton';
import { cn, localToday } from '@/shared/utils/utils';
import { useUser } from '@/shared/hooks/useAuth';
import { materialErrorText, type Tank } from '../hooks/useMaterials';
import { useTankActions, useTankHistory, useTankLevels, type TankLevel } from '../hooks/useTankLevels';
import { CapacitiesModal, GROUP_BAND } from './TankRegisterTab';
import { FieldLabel, Panel, Pill, fmtDateTime, fmtDay, fmtQty } from './materialShared';

const isApprover = (role?: string) => ['superadmin', 'owner', 'admin'].includes(String(role || ''));
const kl = (n: number | null | undefined) => (n === null || n === undefined ? '—' : `${fmtQty(Math.round(n * 1000) / 1000)} KL`);
const signed = (n: number) => `${n > 0 ? '+' : n < 0 ? '−' : ''}${fmtQty(Math.abs(Math.round(n * 1000) / 1000))} KL`;

function fillTone(pct: number | null) {
  if (pct === null) return 'bg-outline-variant';
  if (pct >= 90) return 'bg-danger';
  if (pct <= 10) return 'bg-warning';
  return 'bg-primary';
}

export function TankLevelsTab() {
  const user = useUser();
  const admin = isApprover(user?.role);
  const { data, isLoading } = useTankLevels();
  const [dipFor, setDipFor] = useState<TankLevel | null>(null);
  const [moveFor, setMoveFor] = useState<TankLevel | null>(null);
  const [historyFor, setHistoryFor] = useState<TankLevel | null>(null);
  const [capacities, setCapacities] = useState(false);
  const tanks = data?.tanks || [];
  const totals = data?.totals;
  // CapacitiesModal works on the tank master's shape.
  const asTanks = useMemo(() => tanks.map((t) => ({ _id: t.tankId, code: t.code, name: t.name, group: t.group, groupLabel: t.groupLabel, capacityKl: t.capacityKl, location: '', isActive: true, sortOrder: 0 }) as Tank), [tanks]);

  return (
    <div className="space-y-5">
      <Panel
        title="Tank levels"
        subtitle="Each tank: last dip + oil in − oil out since. Inward into a tank and issues from a tank count on their own."
        actions={admin ? <Button variant="outline" onClick={() => setCapacities(true)}><Gauge className="h-4 w-4" /> Tank capacities</Button> : undefined}
      >
        <div className="grid grid-cols-2 gap-3 p-4 md:grid-cols-4">
          <Figure label="In all tanks" value={kl(totals?.levelKl)} />
          <Figure label="Space left" value={totals?.capacityKl ? kl(totals.spaceKl) : '—'} hint={totals?.capacityKl ? `of ${kl(totals.capacityKl)}` : 'enter capacities'} />
          <Figure label="Tanks without capacity" value={String(totals?.capacityMissing ?? 0)} tone={totals?.capacityMissing ? 'warning' : undefined} />
          <Figure label="Tanks with no reading" value={String(totals?.noReading ?? 0)} tone={totals?.noReading ? 'warning' : undefined} hint="take a dip to start" />
        </div>
        {!!data?.products.length && (
          <div className="flex flex-wrap gap-2 border-t border-border px-4 py-3">
            {data.products.map((p) => (
              <span key={p.product} className="rounded-lg border border-border bg-surface px-2.5 py-1 text-body-sm">
                <b className="font-semibold text-on-surface">{p.product}</b>
                <span className="ml-1.5 font-mono tabular-nums text-on-surface">{kl(p.kl)}</span>
                <span className="ml-1.5 text-data-xs text-on-surface-subtle">{p.tanks.join(', ')}</span>
              </span>
            ))}
          </div>
        )}
      </Panel>

      {isLoading ? (
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3 2xl:grid-cols-4">{Array.from({ length: 8 }).map((_, i) => <Skeleton key={i} className="h-56 rounded-card" />)}</div>
      ) : (
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3 2xl:grid-cols-4">
          {tanks.map((t) => <TankCard key={t.tankId} t={t} onDip={() => setDipFor(t)} onMove={() => setMoveFor(t)} onHistory={() => setHistoryFor(t)} />)}
        </div>
      )}

      {dipFor && <DipModal t={dipFor} onClose={() => setDipFor(null)} />}
      {moveFor && <MoveModal t={moveFor} tanks={tanks} onClose={() => setMoveFor(null)} />}
      {historyFor && <HistoryModal t={historyFor} onClose={() => setHistoryFor(null)} />}
      {capacities && <CapacitiesModal tanks={asTanks} onClose={() => setCapacities(false)} />}
    </div>
  );
}

function Figure({ label, value, hint, tone }: { label: string; value: string; hint?: string; tone?: 'warning' }) {
  return (
    <div className={cn('rounded-lg border px-3 py-2.5', tone === 'warning' ? 'border-warning/30 bg-warning/10' : 'border-border bg-surface')}>
      <div className="text-data-xs font-semibold uppercase tracking-wide text-on-surface-variant">{label}</div>
      <div className="mt-0.5 font-mono text-data-lg font-semibold tabular-nums text-on-surface">{value}</div>
      {hint && <div className="text-data-xs text-on-surface-subtle">{hint}</div>}
    </div>
  );
}

function TankCard({ t, onDip, onMove, onHistory }: { t: TankLevel; onDip: () => void; onMove: () => void; onHistory: () => void }) {
  const pct = t.fillPct;
  return (
    <section className="flex flex-col overflow-hidden rounded-card border border-border bg-card shadow-surface-1">
      <div className={cn('flex items-center justify-between gap-2 px-3 py-2', GROUP_BAND[t.group])}>
        <span className="text-body-md font-bold">{t.code}</span>
        <span className="truncate text-data-xs font-semibold" title={t.product || 'No product recorded yet'}>{t.product || 'Product not recorded'}</span>
      </div>
      <div className="flex flex-1 gap-3 p-3">
        {/* Gauge */}
        <div className="relative h-28 w-9 shrink-0 overflow-hidden rounded-md border border-border bg-surface-container-low" title={pct === null ? 'Enter the capacity to see how full' : `${pct}% full`}>
          <div className={cn('absolute inset-x-0 bottom-0 transition-all', fillTone(pct))} style={{ height: `${pct ?? 0}%` }} />
          {pct !== null && <span className="absolute inset-x-0 top-1 text-center text-[10px] font-bold text-on-surface">{pct}%</span>}
        </div>
        <div className="min-w-0 flex-1 space-y-1">
          <div className="font-mono text-data-lg font-semibold tabular-nums text-on-surface">
            {t.levelKl === null ? <span className="text-body-sm font-sans font-medium text-on-surface-subtle">No reading yet</span> : <>{t.estimated && '≈ '}{kl(t.levelKl)}</>}
          </div>
          <div className="text-data-xs text-on-surface-variant">
            {t.capacityKl ? <>Capacity {kl(t.capacityKl)} · <b className="font-semibold text-on-surface">space {kl(t.spaceKl)}</b></> : <span className="text-warning-text">Capacity not entered</span>}
          </div>
          <div className="text-data-xs text-on-surface-variant">
            {t.lastDip ? <>Last dip {fmtDay(t.lastDip.dateKey)}: {kl(t.lastDip.kl)}</> : !t.latestEntry && <span className="text-warning-text">No dip yet — counted from movements</span>}
          </div>
          {/* A register entry without a quantity ("sn70") is still shown — it is saved. */}
          {t.latestEntry && t.latestEntry.kl === null && (
            <div className="rounded-md bg-warning/10 px-2 py-1 text-data-xs text-warning-text">
              Register {fmtDay(t.latestEntry.dateKey)}: <b className="font-semibold">“{t.latestEntry.raw}”</b> — no quantity written. Use <b className="font-semibold">Dip</b> to add the KL (or Full / Empty).
            </div>
          )}
          {(t.inSinceDipKl > 0 || t.outSinceDipKl > 0) && (
            <div className="text-data-xs">
              {t.inSinceDipKl > 0 && <span className="text-success-text">+{fmtQty(t.inSinceDipKl)} KL in</span>}
              {t.inSinceDipKl > 0 && t.outSinceDipKl > 0 && <span className="mx-1 text-on-surface-subtle">·</span>}
              {t.outSinceDipKl > 0 && <span className="text-danger-text">−{fmtQty(t.outSinceDipKl)} KL out</span>}
              <span className="text-on-surface-subtle">{t.hasDip ? ' since the dip' : ' so far'}</span>
            </div>
          )}
          {t.lastVarianceKl !== null && t.lastVarianceKl !== 0 && (
            <Pill tone={t.lastVarianceKl < 0 ? 'danger' : 'info'} title="Last dip compared with what the book expected">
              Dip vs book {signed(t.lastVarianceKl)}
            </Pill>
          )}
          {t.belowZero && <Pill tone="warning" title="More went out than the book shows came in — take a dip">Book below zero — take a dip</Pill>}
        </div>
      </div>
      <div className="grid grid-cols-3 border-t border-border text-body-sm font-semibold">
        <button type="button" onClick={onDip} className="flex items-center justify-center gap-1.5 py-2 text-primary hover:bg-surface-container-low"><Ruler className="h-3.5 w-3.5" />Dip</button>
        <button type="button" onClick={onMove} className="flex items-center justify-center gap-1.5 border-x border-border py-2 text-primary hover:bg-surface-container-low"><ArrowRightLeft className="h-3.5 w-3.5" />In / Out</button>
        <button type="button" onClick={onHistory} className="flex items-center justify-center gap-1.5 py-2 text-primary hover:bg-surface-container-low"><History className="h-3.5 w-3.5" />History</button>
      </div>
    </section>
  );
}

function DipModal({ t, onClose }: { t: TankLevel; onClose: () => void }) {
  const { dip } = useTankActions();
  const [value, setValue] = useState('');
  const [product, setProduct] = useState(t.product);
  const [date, setDate] = useState(localToday());
  const submit = async () => {
    try {
      const r = await dip.mutateAsync({ tankId: t.tankId, kl: Number(value), product, date });
      const v = r.varianceKl;
      toast.success(`${t.code}: ${kl(r.levelKl)} recorded${v ? ` — ${signed(v)} against the book` : ''}`);
      onClose();
    } catch (err) { toast.error(materialErrorText(err, 'Could not record the dip.')); }
  };
  return (
    <Modal open onClose={onClose} size="md" title={`Dip reading — ${t.code}`} footer={(
      <><Button variant="outline" onClick={onClose}>Cancel</Button><Button onClick={submit} loading={dip.isPending} disabled={value === '' || Number(value) < 0}>Save reading</Button></>
    )}>
      <div className="space-y-3">
        <p className="text-body-sm text-on-surface-variant">
          The KL measured in the tank now. It goes into the Tank Register for the day; the level is counted from it.
          {t.levelKl !== null && <> The book expects <b className="font-semibold text-on-surface">{kl(t.levelKl)}</b>.</>}
        </p>
        <div className="flex gap-2">
          <Button type="button" variant="outline" size="sm" disabled={!t.capacityKl} title={t.capacityKl ? `Full = ${t.capacityKl} KL` : 'Enter the tank capacity first'} onClick={() => setValue(String(t.capacityKl))}>Full</Button>
          <Button type="button" variant="outline" size="sm" onClick={() => setValue('0')}>Empty</Button>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <Input label="KL measured" type="number" min="0" step="any" value={value} onChange={(e) => setValue(e.target.value)} placeholder="e.g. 12.5 (0 = empty)" autoFocus />
          <Input label="Date" type="date" value={date} max={localToday()} onChange={(e) => setDate(e.target.value)} />
        </div>
        <Input label="Product in the tank" value={product} onChange={(e) => setProduct(e.target.value)} placeholder="e.g. SN70, SN 150, 350" />
        {value !== '' && t.levelKl !== null && Number(value) >= 0 && (
          <div className={cn('rounded-lg px-3 py-2 text-body-sm', Number(value) < t.levelKl ? 'bg-danger/10 text-danger-text' : 'bg-info/10 text-info-text')}>
            Against the book: {signed(Number(value) - t.levelKl)}
          </div>
        )}
      </div>
    </Modal>
  );
}

function MoveModal({ t, tanks, onClose }: { t: TankLevel; tanks: TankLevel[]; onClose: () => void }) {
  const { movement } = useTankActions();
  const [direction, setDirection] = useState<'in' | 'out' | 'transfer'>('in');
  const [quantity, setQuantity] = useState('');
  const [unit, setUnit] = useState<'KL' | 'LTR' | 'KG'>('LTR');
  const [density, setDensity] = useState('');
  const [product, setProduct] = useState(t.product);
  const [toTankId, setToTankId] = useState('');
  const [note, setNote] = useState('');
  const [date, setDate] = useState(localToday());
  const ready = Number(quantity) > 0 && (direction !== 'out' || note.trim()) && (direction !== 'transfer' || toTankId);
  const submit = async () => {
    try {
      await movement.mutateAsync({
        tankId: t.tankId, direction, quantity: Number(quantity), unit, product, note, date,
        ...(unit === 'KG' && Number(density) > 0 ? { density: Number(density) } : {}),
        ...(direction === 'transfer' ? { toTankId } : {}),
      });
      toast.success(direction === 'transfer' ? `Moved to ${tanks.find((x) => x.tankId === toTankId)?.code}` : direction === 'in' ? `Added to ${t.code}` : `Taken out of ${t.code}`);
      onClose();
    } catch (err) { toast.error(materialErrorText(err, 'Could not save.')); }
  };
  const choice = (value: typeof direction, label: string, Icon: typeof ArrowDownToLine) => (
    <button type="button" onClick={() => setDirection(value)}
      className={cn('flex flex-1 items-center justify-center gap-1.5 rounded-lg border px-3 py-2 text-body-sm font-semibold', direction === value ? 'border-primary bg-primary/10 text-primary' : 'border-border text-on-surface-variant hover:bg-surface-container-low')}>
      <Icon className="h-4 w-4" />{label}
    </button>
  );
  return (
    <Modal open onClose={onClose} size="md" title={`Oil in / out — ${t.code}`} footer={(
      <><Button variant="outline" onClick={onClose}>Cancel</Button><Button onClick={submit} loading={movement.isPending} disabled={!ready}>Save</Button></>
    )}>
      <div className="space-y-3">
        <div className="flex gap-2">
          {choice('in', 'Add oil', ArrowDownToLine)}
          {choice('out', 'Take out', ArrowUpFromLine)}
          {choice('transfer', 'Transfer', ArrowRightLeft)}
        </div>
        <p className="text-data-xs text-on-surface-variant">Material Inward into a tank and requests issued from a tank are already counted — use this for anything else.</p>
        <div className="grid grid-cols-2 gap-3">
          <Input label="Quantity" type="number" min="0" step="any" value={quantity} onChange={(e) => setQuantity(e.target.value)} autoFocus />
          <label className="block"><FieldLabel>Unit</FieldLabel>
            <Select value={unit} onChange={(e) => setUnit(e.target.value as typeof unit)} options={[{ value: 'LTR', label: 'Litres' }, { value: 'KL', label: 'KL' }, { value: 'KG', label: 'KG' }]} />
          </label>
        </div>
        {unit === 'KG' && <Input label="Density (kg per litre)" type="number" min="0.6" max="2" step="0.001" value={density} onChange={(e) => setDensity(e.target.value)} placeholder="blank = 0.87 average" />}
        {direction === 'transfer' && (
          <label className="block"><FieldLabel required>Into tank</FieldLabel>
            <Select value={toTankId} onChange={(e) => setToTankId(e.target.value)} placeholder="Choose the tank"
              options={tanks.filter((x) => x.tankId !== t.tankId).map((x) => ({ value: x.tankId, label: `${x.code}${x.product ? ` · ${x.product}` : ''}${x.spaceKl !== null ? ` · space ${kl(x.spaceKl)}` : ''}` }))} />
          </label>
        )}
        <div className="grid grid-cols-2 gap-3">
          <Input label="Product" value={product} onChange={(e) => setProduct(e.target.value)} placeholder="e.g. SN70" />
          <Input label="Date" type="date" value={date} max={localToday()} onChange={(e) => setDate(e.target.value)} />
        </div>
        <Input label={direction === 'out' ? 'Where did it go? *' : 'Note'} value={note} onChange={(e) => setNote(e.target.value)} placeholder={direction === 'out' ? 'e.g. filling for SO/2026-27/0300, drained, sample' : 'optional'} />
      </div>
    </Modal>
  );
}

function HistoryModal({ t, onClose }: { t: TankLevel; onClose: () => void }) {
  const { data, isLoading } = useTankHistory(t.tankId);
  const rows = data?.rows || [];
  return (
    <Modal open onClose={onClose} size="2xl" title={`${t.code} — history`}>
      {isLoading ? <Skeleton className="h-40" /> : !rows.length ? (
        <div className="py-8 text-center text-body-sm text-on-surface-variant">Nothing recorded for this tank yet.</div>
      ) : (
        <div className="max-h-[60vh] overflow-auto rounded-lg border border-border">
          <table className="w-full min-w-[720px] text-body-sm">
            <thead className="sticky top-0 bg-surface-container-low text-left text-label-caps uppercase text-on-surface-variant">
              <tr>
                <th className="px-3 py-2">Date</th>
                <th className="px-3 py-2">What</th>
                <th className="px-3 py-2 text-right">KL</th>
                <th className="px-3 py-2 text-right">Level after</th>
                <th className="px-3 py-2">By</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {rows.map((r, i) => (
                <tr key={`${r.at}-${i}`} className="align-top">
                  <td className="whitespace-nowrap px-3 py-2 text-on-surface-variant">{fmtDay(r.dateKey)}<div className="text-data-xs text-on-surface-subtle">{fmtDateTime(r.at)}</div></td>
                  <td className="px-3 py-2">
                    {r.type === 'dip' ? (
                      <><b className="font-semibold text-on-surface">Dip reading</b> <span className="text-on-surface-variant">“{r.raw}”</span>
                        {r.variance !== null && r.variance !== 0 && <div className={cn('text-data-xs font-semibold', r.variance < 0 ? 'text-danger-text' : 'text-info-text')}>{signed(r.variance)} against the book</div>}</>
                    ) : (
                      <><b className="font-semibold text-on-surface">{r.reasonLabel}</b>{r.referenceNumber && <span className="ml-1 font-mono text-data-xs text-on-surface-variant">{r.referenceNumber}</span>}
                        <div className="text-data-xs text-on-surface-variant">{[r.product, r.quantity !== null && r.unit !== 'KL' ? `${fmtQty(r.quantity)} ${r.unit}${r.estimated ? ' (density estimated)' : ''}` : '', r.note].filter(Boolean).join(' · ')}</div></>
                    )}
                  </td>
                  <td className={cn('whitespace-nowrap px-3 py-2 text-right font-mono tabular-nums', r.type === 'in' ? 'text-success-text' : r.type === 'out' ? 'text-danger-text' : 'text-on-surface')}>
                    {r.kl === null ? '—' : r.type === 'dip' ? fmtQty(r.kl) : `${r.type === 'in' ? '+' : '−'}${fmtQty(r.kl)}`}
                  </td>
                  <td className="whitespace-nowrap px-3 py-2 text-right font-mono tabular-nums text-on-surface">{r.levelAfter === null ? '—' : fmtQty(r.levelAfter)}</td>
                  <td className="px-3 py-2 text-on-surface-variant">{r.byName || '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </Modal>
  );
}

export default TankLevelsTab;
